import { useCallback, useState } from 'react'
import { CircleAlert, Download } from 'lucide-react'
import {
  useV1ReportingBudgetEntriesList,
  v1ReportingBudgetEntriesExportRetrieve,
} from '@/api/client'
import { type BudgetEntryReportRow, type V1ReportingBudgetEntriesListParams } from '@/api/model'
import { DataTable, type Column } from '@/components/data-table'
import {
  EMPTY_STUDENT_DATA_REPORT_FILTERS,
  type StudentDataReportFilterValues,
} from '@/components/reporting/student-data-report-filter-values'
import { StudentDataReportFilters } from '@/components/reporting/student-data-report-filters'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { filenameFromDisposition, saveBlob } from '@/lib/download-file'
import { queryHasHttpError } from '@/lib/query-state'
import {
  formatBudgetAmount,
  formatReportDateTime,
  hasBudgetEntryReportFilters,
  omitPagination,
  reportValueLabel,
} from '@/lib/reporting-budget-entries'

const PAGE_SIZE = 25
const ALL = '__all__'

const columns: Column<BudgetEntryReportRow>[] = [
  {
    header: 'Wpis / uczeń',
    sortKey: 'name',
    sortLabel: 'nazwa wpisu',
    className: 'min-w-48 whitespace-normal',
    cell: (row) => (
      <div className="flex max-w-72 flex-col gap-0.5">
        <span className="font-medium">{row.name || 'Bez nazwy'}</span>
        {row.note ? (
          <span className="line-clamp-2 text-xs text-muted-foreground" title={row.note}>
            {row.note}
          </span>
        ) : null}
        <span className="font-mono text-xs text-muted-foreground">{row.anonymous_student_id}</span>
      </div>
    ),
  },
  {
    header: 'Szkoła / klasa',
    sortKey: 'school_name',
    sortLabel: 'nazwa szkoły',
    className: 'min-w-40 whitespace-normal',
    cell: (row) => (
      <div className="flex max-w-64 flex-col gap-0.5">
        <span className="font-medium">{row.school_name ?? '—'}</span>
        <span className="text-xs text-muted-foreground">
          {[row.class_name, row.school_code].filter(Boolean).join(' · ') || '—'}
        </span>
      </div>
    ),
  },
  {
    header: 'Data',
    sortKey: 'occurred_at',
    cell: (row) => formatReportDateTime(row.occurred_at),
  },
  {
    header: 'Typ operacji',
    sortKey: 'kind',
    className: 'min-w-32 whitespace-normal',
    cell: (row) => (
      <div className="flex max-w-52 flex-col items-start gap-1">
        <span>{reportValueLabel(row.kind_label, row.kind)}</span>
        {row.income_kind ? (
          <span className="text-xs text-muted-foreground">
            {reportValueLabel(row.income_kind_label, row.income_kind)}
            {row.regular === true ? ' · regularny' : ''}
          </span>
        ) : null}
        {row.is_balance_adjustment ? <Badge variant="outline">Korekta salda</Badge> : null}
      </div>
    ),
  },
  {
    header: 'Kwota',
    sortKey: 'amount_minor_units',
    className: 'text-right tabular-nums',
    cell: (row) => formatBudgetAmount(row.amount_minor_units),
  },
  {
    header: 'Oznaczenia',
    sortKey: 'category',
    sortLabel: 'kategoria',
    className: 'min-w-28 whitespace-normal',
    cell: (row) => (
      <div className="flex max-w-56 flex-col gap-0.5">
        <span>{reportValueLabel(row.category_label, row.category)}</span>
        {row.classification ? (
          <span className="text-xs text-muted-foreground">
            {reportValueLabel(row.classification_label, row.classification)}
          </span>
        ) : null}
      </div>
    ),
  },
  {
    header: 'Emocja',
    sortKey: 'emotion',
    className: 'min-w-36 whitespace-normal',
    cell: (row) => (
      <div className="flex max-w-64 flex-col gap-0.5">
        <span>{reportValueLabel(row.emotion_label, row.emotion)}</span>
        {row.emotion_note ? (
          <span className="line-clamp-2 text-xs text-muted-foreground" title={row.emotion_note}>
            {row.emotion_note}
          </span>
        ) : null}
      </div>
    ),
  },
]

export function BudgetEntriesReport() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [ordering, setOrdering] = useState('-occurred_at')
  const [filters, setFilters] = useState<StudentDataReportFilterValues>(
    EMPTY_STUDENT_DATA_REPORT_FILTERS,
  )

  const selectedSchoolId = filters.schoolId === ALL ? undefined : Number(filters.schoolId)

  const params = {
    page,
    page_size: PAGE_SIZE,
    search: search.trim() || undefined,
    ordering:
      ordering === ''
        ? undefined
        : (ordering as NonNullable<V1ReportingBudgetEntriesListParams['ordering']>),
    school_id: selectedSchoolId,
    class_id: filters.classId === ALL ? undefined : Number(filters.classId),
    date_from: filters.dateFrom || undefined,
    date_to: filters.dateTo || undefined,
    kind:
      filters.kind === ALL
        ? undefined
        : (filters.kind as NonNullable<V1ReportingBudgetEntriesListParams['kind']>),
    income_kind:
      filters.incomeKind === ALL
        ? undefined
        : (filters.incomeKind as NonNullable<V1ReportingBudgetEntriesListParams['income_kind']>),
    category:
      filters.category === ALL
        ? undefined
        : (filters.category as NonNullable<V1ReportingBudgetEntriesListParams['category']>),
    classification:
      filters.classification === ALL
        ? undefined
        : (filters.classification as NonNullable<
            V1ReportingBudgetEntriesListParams['classification']
          >),
    emotion:
      filters.emotion === ALL
        ? undefined
        : (filters.emotion as NonNullable<V1ReportingBudgetEntriesListParams['emotion']>),
  }

  const query = useV1ReportingBudgetEntriesList(params)
  const data = query.data?.status === 200 ? query.data.data : undefined

  const filtering = hasBudgetEntryReportFilters({
    search,
    ...filters,
  })

  const [downloadingVisible, setDownloadingVisible] = useState(false)
  const [visibleFailed, setVisibleFailed] = useState(false)

  const downloadVisible = async () => {
    setVisibleFailed(false)
    setDownloadingVisible(true)
    try {
      const response = await v1ReportingBudgetEntriesExportRetrieve(omitPagination(params))
      if (response.status !== 200) {
        setVisibleFailed(true)
        return
      }
      const disposition = response.headers.get('content-disposition')
      saveBlob(response.data, filenameFromDisposition(disposition, 'wpisy-budzetowe.xlsx'))
    } catch {
      setVisibleFailed(true)
    } finally {
      setDownloadingVisible(false)
    }
  }

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
    setPage(1)
  }, [])

  const handleFiltersChange = useCallback((value: StudentDataReportFilterValues) => {
    setFilters(value)
    setPage(1)
  }, [])

  return (
    <section
      aria-labelledby="student-budget-report-title"
      className="flex min-w-0 flex-col gap-4"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 id="student-budget-report-title" className="font-heading text-xl font-semibold">
            Wydatki i przychody uczniów
          </h2>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Anonimowe wpisy budżetowe z wyszukiwaniem i filtrowaniem po najważniejszych
            właściwościach.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data ? (
            <Badge variant="secondary" className="w-fit">
              {data.count === 1 ? '1 wpis' : `${data.count} wpisów`}
            </Badge>
          ) : null}
          <Button onClick={downloadVisible} disabled={downloadingVisible} variant="outline">
            {downloadingVisible ? (
              <Spinner data-icon="inline-start" />
            ) : (
              <Download data-icon="inline-start" />
            )}
            {downloadingVisible ? 'Przygotowuję plik…' : 'Eksportuj widoczne (XLSX)'}
          </Button>
        </div>
      </div>

      {visibleFailed ? (
        <Alert variant="destructive" className="max-w-2xl">
          <CircleAlert />
          <AlertTitle>Nie udało się pobrać pliku</AlertTitle>
          <AlertDescription>Spróbuj ponownie za chwilę.</AlertDescription>
        </Alert>
      ) : null}

      <StudentDataReportFilters
        search={search}
        onSearchChange={handleSearchChange}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      <DataTable
        columns={columns}
        rows={data?.results ?? []}
        rowKey={(row) => row.id}
        count={data?.count ?? 0}
        page={page}
        onPageChange={setPage}
        pageSize={PAGE_SIZE}
        search={search}
        onSearchChange={handleSearchChange}
        hideSearch
        ordering={ordering}
        onOrderingChange={(value) => {
          setOrdering(value)
          setPage(1)
        }}
        isLoading={query.isLoading}
        isError={queryHasHttpError(query)}
        errorMessage="Nie udało się pobrać wpisów uczniów. Spróbuj ponownie."
        onRetry={() => void query.refetch()}
        emptyMessage={
          filtering
            ? 'Brak wpisów spełniających wybrane kryteria.'
            : 'Uczniowie nie dodali jeszcze żadnych wydatków ani przychodów.'
        }
        countNoun={['wpis', 'wpisów']}
      />
    </section>
  )
}
